import { capitalize, provide, type InjectionKey } from 'vue'
import type { PosthogInstance } from '@/lib/analytics'
import type { Mode } from '@/types'
import { snakeCase } from 'change-case'

let trackerInstance: PosthogInstance | undefined

export async function setupTracker(partyId: string, workflowName: string, mode: Mode, workspaceId: string, clientDomain?: string) {
    const { setupPosthog } = await import('@/lib/analytics')
    trackerInstance = setupPosthog(partyId, workflowName, mode, workspaceId, clientDomain)
}

type Props = {
    [key: string]: unknown
    stacktrace?: unknown
}

function formatObjectName(task: string, provider?: string) {
    if (provider) {
        return provider + capitalize(task)
    }
    return task || ''
}

function propsObjToSnakeCases(props: Props) {
    return Object.keys(props).reduce((newObject, currentKey) => ({
        ...newObject,
        [snakeCase(currentKey)]: props[currentKey as keyof Props],
    }), {})
}

export type Track = (action: string, route?: string, _props?: Props) => void

function useTracker(taskName: string, providerName?: string, injectionKey?: InjectionKey<Track>) {
    const track: Track = (action, route, _props) => {
        const app = formatObjectName(taskName, providerName)
        const eventName = ['widget', app, action].join(':')
        const props = {
            eventName,
            taskName,
            route,
            $timestamp: new Date().toISOString(),
            ..._props,
        }

        trackerInstance?.track(eventName, propsObjToSnakeCases(props))
    }

    if (injectionKey) {
        provide(injectionKey, track)
    }

    return {
        track,
    }
}

export default useTracker
